/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Input, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { InputRow, TableDraggableColumn, TableDraggableRows } from '@g17eco/molecules/table-draggable-rows';
import { ScopeWheelPreferences } from '@g17eco/types/survey';
import G17Client from '../../../../services/G17Client';
import { addSiteAlert, SiteAlertColors } from '../../../../slice/siteAlertsSlice';
import { QUESTION } from '@constants/terminology';
import { useAppDispatch } from '@reducers/index';

interface QuestionProgressEditModalProps {
  surveyId: string;
  toggle: () => void;
  scopeWheelPreferences: ScopeWheelPreferences[];
  handleReload: () => Promise<void>;
}

export const QuestionProgressEditModal = (props: QuestionProgressEditModalProps) => {
  const { surveyId, toggle, scopeWheelPreferences, handleReload } = props;

  const dispatch = useAppDispatch();
  const [rowOrder, setRowOrder] = useState<string[]>([]);
  const [visibleMap, setVisibleMap] = useState<Map<string, { name: string, visible: boolean }>>(new Map());
  const [isDirty, setDirty] = useState(false);

  const closeModal = () => {
    if (!isDirty || window.confirm('Are you sure you want to leave this page? All your changes will be lost.')) {
      toggle();
    }
  }

  useEffect(() => {
    const map = new Map<string, { name: string, visible: boolean }>();
    scopeWheelPreferences.forEach(scopeWheel => {
      map.set(scopeWheel.scopeCode, { name: scopeWheel.name, visible: scopeWheel.visible !== false });
    });
    setVisibleMap(map);
    setRowOrder(Array.from(map.keys()));
  }, [scopeWheelPreferences]);

  const handleUpdateRowOrder = (codes: string[]) => {
    setDirty(true);
    setRowOrder(codes);
  }

  const handleVisibileUpdate = useCallback((code: string, visible: boolean) => {
    setDirty(true);
    setVisibleMap(prevMap => {
      const newMap = new Map(prevMap);
      const oldVisible = prevMap.get(code);
      if (!oldVisible) {
        return prevMap;
      }
      newMap.set(code, {
        ...oldVisible,
        visible
      });
      return newMap;
    });
  }, [setVisibleMap]);

  const rows: InputRow[] = useMemo(() => {
    return rowOrder.map(scopeCode => {
      const { name, visible } = visibleMap.get(scopeCode) ?? { visible: true, name: scopeCode };
      return {
        id: scopeCode,
        cols: [
          name,
          <Input
            key={`visibility-input-${scopeCode}`}
            type='checkbox'
            checked={!visible}
            onChange={(e) => {
              handleVisibileUpdate(scopeCode, !e.currentTarget.checked)
            }}
          />
        ]
      }
    });
  }, [rowOrder, visibleMap, handleVisibileUpdate]);

  const handleUpdate = () => {
    const data: ScopeWheelPreferences[] = rowOrder.map(scopeCode => {
      const { name, visible } = visibleMap.get(scopeCode) ?? { visible: true, name: scopeCode };
      return {
        name,
        scopeCode,
        visible
      }
    });

    G17Client.updateSurveyScopeWheels(surveyId, data)
      .then(() => {
        dispatch(addSiteAlert({
          content: `${QUESTION.CAPITALIZED_SINGULAR} progress wheel preferences saved successfully.`,
          timeout: 5000
        }));
        return handleReload();
      })
      .then(() => {
        setDirty(false);
        toggle();
      }).catch(() => {
        dispatch(addSiteAlert({
          content: `There was a problem updating your ${QUESTION.CAPITALIZED_SINGULAR} Progress settings. Please try again later.`,
          color: SiteAlertColors.Danger,
        }));
      });
  }

  const columns: TableDraggableColumn[] = [{
    header: 'Scope name',
    className: ''
  },
  {
    header: 'Hidden',
    className: 'col-checkbox'
  }];

  return (
    <Modal className='question-progress-edit-modal' isOpen={true} toggle={closeModal} backdrop='static'>
      <ModalHeader toggle={closeModal}>Edit progress wheels</ModalHeader>
      <ModalBody>
        <TableDraggableRows columns={columns} data={rows} handleUpdate={handleUpdateRowOrder} />
      </ModalBody>
      <ModalFooter>
        <Button color='link-secondary' onClick={closeModal}>Close</Button>
        <Button color='primary' onClick={handleUpdate}>Update</Button>
      </ModalFooter>
    </Modal>
  )
}
